import type { ValueTypeEntry } from "../index";
import { CurrencyEditor } from "./currency-editor";

/**
 * Canonical currency value. `amount` is an integer in the currency's minor
 * units (e.g. cents for USD, yen for JPY, fils for BHD). `currency_code`
 * is the ISO-4217 code.
 *
 * Legacy fixtures may carry `currency` instead of `currency_code` — we
 * accept that key on input, but ALWAYS emit `currency_code` on output.
 */
export type CurrencyValue = { amount: number; currency_code: string } | null;

interface CurrencyOptions {
  locale?: string;
}

export const CURRENCY_DEFAULT_CODE = "USD";

export function normalizeCurrencyInput(
  value: unknown,
): { amount: number; currency_code: string } | null {
  if (value == null) return null;
  // Bare number → legacy minor-unit USD value. This shape comes from
  // older grid cells that stored currency as a plain `number` (no code).
  // Keep that minor-unit contract so legacy values do not display 100x too
  // large while newer object-shaped values continue to carry their own code.
  if (typeof value === "number" && Number.isFinite(value)) {
    return { amount: Math.round(value), currency_code: CURRENCY_DEFAULT_CODE };
  }
  if (typeof value !== "object") return null;
  const obj = value as { amount?: unknown; currency_code?: unknown; currency?: unknown };
  if (typeof obj.amount !== "number" || !Number.isFinite(obj.amount)) return null;
  const code =
    typeof obj.currency_code === "string" && obj.currency_code
      ? obj.currency_code
      : typeof obj.currency === "string" && obj.currency
        ? obj.currency
        : null;
  if (!code) return null;
  return { amount: obj.amount, currency_code: code };
}

// Module-level caches for `Intl.NumberFormat` instances. Constructing a
// formatter is expensive relative to `.format()` / `.resolvedOptions()`, so
// keying by `locale|currency` keeps the hot path allocation-free across
// renders without changing output semantics.
const CURRENCY_FMT_CACHE = new Map<string, Intl.NumberFormat>();
const PLAIN_FMT_CACHE = new Map<string, Intl.NumberFormat>();

/**
 * Test-only: clear the module-level formatter caches. Production code must
 * not call this — the caches are intentionally process-lived. Tests that
 * stub `Intl.NumberFormat` need this to avoid hitting a cached instance
 * constructed before the stub was installed.
 */
export function __resetCurrencyFormatterCachesForTests(): void {
  CURRENCY_FMT_CACHE.clear();
  PLAIN_FMT_CACHE.clear();
}

function getCurrencyFormatter(locale: string | undefined, code: string): Intl.NumberFormat {
  const key = `${locale ?? ""}|${code}`;
  const hit = CURRENCY_FMT_CACHE.get(key);
  if (hit) return hit;
  const next = new Intl.NumberFormat(locale, { style: "currency", currency: code });
  CURRENCY_FMT_CACHE.set(key, next);
  return next;
}

function getPlainFormatter(locale: string | undefined): Intl.NumberFormat {
  const key = locale ?? "";
  const hit = PLAIN_FMT_CACHE.get(key);
  if (hit) return hit;
  const next = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  PLAIN_FMT_CACHE.set(key, next);
  return next;
}

/**
 * Resolved minor-unit digit count for a given ISO-4217 code at the given
 * locale. Uses `Intl.NumberFormat(...).resolvedOptions()` — never hardcoded.
 * JPY = 0, USD/EUR = 2, BHD = 3.
 */
export function resolveCurrencyMinorDigits(code: string, locale?: string): number {
  try {
    const opts = getCurrencyFormatter(locale, code).resolvedOptions();
    return opts.maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

export function formatCurrencyMajorValue(
  major: number,
  currency_code: string,
  locale?: string,
): string {
  if (!Number.isFinite(major)) return "";
  try {
    return getCurrencyFormatter(locale, currency_code).format(major);
  } catch {
    return `${getPlainFormatter(locale).format(major)} ${currency_code}`;
  }
}

function formatCurrencyValue(value: CurrencyValue, locale?: string): string {
  const normalized = normalizeCurrencyInput(value);
  if (normalized == null) return "";
  const { amount, currency_code } = normalized;
  if (!Number.isFinite(amount)) return "";
  const digits = resolveCurrencyMinorDigits(currency_code, locale);
  const major = amount / 10 ** digits;
  return formatCurrencyMajorValue(major, currency_code, locale);
}

export function toCurrencyInputString(amount: number, code: string, locale?: string): string {
  if (!Number.isFinite(amount)) return "";
  const digits = resolveCurrencyMinorDigits(code, locale);
  const major = amount / 10 ** digits;
  // Render as plain decimal — no grouping — so the input is editable.
  return major.toFixed(digits);
}

export function fromCurrencyInputString(
  str: string,
  code: string,
  locale?: string,
): { ok: true; amount: number } | { ok: false } {
  const trimmed = str.trim();
  if (trimmed === "") return { ok: false };
  // Accept either dot or comma as decimal separator for tolerance.
  const normalized = trimmed.replace(/,/g, ".");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return { ok: false };
  const digits = resolveCurrencyMinorDigits(code, locale);
  return { ok: true, amount: Math.round(parsed * 10 ** digits) };
}

export const currencyType: ValueTypeEntry<CurrencyValue, CurrencyOptions> = {
  name: "currency",
  format(value, options) {
    return formatCurrencyValue(value, options?.locale);
  },
  render({ value, options }) {
    return <span>{formatCurrencyValue(value, options?.locale)}</span>;
  },
  display({ value, options }) {
    // `value` may arrive as a raw number (legacy minor-units) or as the
    // canonical object shape — `normalizeCurrencyInput` (called by
    // `formatCurrencyValue`) accepts both.
    return <span>{formatCurrencyValue(value as CurrencyValue, options?.locale)}</span>;
  },
  edit({ value, options, onCommit, onCancel }) {
    return (
      <CurrencyEditor
        value={value}
        {...(options?.locale !== undefined ? { locale: options.locale } : {})}
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  },
};

export type { CurrencyOptions };
